import type {RoadmapAllocationsResponse} from './api';
import type {RoadmapDataset, RoadmapItem} from './roadmapData';
import {buildRoadmapDatasetFromItems} from './roadmapData';
import {UNCATEGORIZED_INVESTMENT_CATEGORY} from './types';

export interface MyRoadmapIdentity {
  employeeId?: string | null;
  email?: string | null;
  employeeIds?: readonly (string | null | undefined)[];
  emails?: readonly (string | null | undefined)[];
}

const normalizeIdentityValue = (value: string | null | undefined) =>
  value?.trim().toLocaleLowerCase() || null;

const normalizedIdentityCache = new WeakMap<
  MyRoadmapIdentity,
  {employeeIds: Set<string>; emails: Set<string>}
>();

const getNormalizedIdentity = (identity: MyRoadmapIdentity) => {
  const cached = normalizedIdentityCache.get(identity);
  if (cached) return cached;

  const normalized = {
    employeeIds: new Set(
      [identity.employeeId, ...(identity.employeeIds ?? [])]
        .map(normalizeIdentityValue)
        .filter((value): value is string => value !== null)
    ),
    emails: new Set(
      [identity.email, ...(identity.emails ?? [])]
        .map(normalizeIdentityValue)
        .filter((value): value is string => value !== null)
    ),
  };
  normalizedIdentityCache.set(identity, normalized);
  return normalized;
};

export const isMyRoadmapInitiative = (initiative: RoadmapItem, identity: MyRoadmapIdentity) => {
  const {employeeIds, emails} = getNormalizedIdentity(identity);
  const initiativeEmployeeId = normalizeIdentityValue(initiative.employeeId);
  const initiativeEmail = normalizeIdentityValue(initiative.assigneeEmail);

  return Boolean(
    (initiativeEmployeeId && employeeIds.has(initiativeEmployeeId)) ||
    (initiativeEmail && emails.has(initiativeEmail))
  );
};

export const buildMyRoadmapDataset = (
  dataset: RoadmapDataset,
  identity: MyRoadmapIdentity
): RoadmapDataset => {
  const includedKeys = new Set<string>();

  dataset.initiatives.forEach((initiative) => {
    if (!isMyRoadmapInitiative(initiative, identity)) return;
    includedKeys.add(initiative.key);
    initiative.ancestorKeys.forEach((key) => includedKeys.add(key));

    let parentKey = initiative.parentKey;
    while (parentKey && !includedKeys.has(parentKey)) {
      includedKeys.add(parentKey);
      parentKey = dataset.itemsByKey.get(parentKey)?.parentKey ?? null;
    }
  });

  return buildRoadmapDatasetFromItems(dataset.items.filter((item) => includedKeys.has(item.key)));
};

export const buildMyRoadmapAllocations = (
  initiatives: RoadmapItem[],
  quarterEstimateKey: 'q1' | 'q2' | 'q3' | 'q4'
): RoadmapAllocationsResponse => {
  const workstreams = new Map<string, RoadmapAllocationsResponse['workstreams'][number]>();
  let totalHeadcount = 0;
  let hasHeadcount = false;
  const investmentCategories: Record<string, number> = {};

  initiatives.forEach((initiative) => {
    const key = initiative.subWorkstreamKey ?? initiative.narrativeKey ?? 'unassigned';
    const title = initiative.subWorkstreamTitle ?? initiative.narrativeTitle ?? 'Unassigned';
    const existing = workstreams.get(key) ?? {
      jira_key: key,
      title,
      initiative_count: 0,
      total_headcount: null,
      investment_categories: {},
      dev_estimates: {q1: 0, q2: 0, q3: 0, q4: 0},
    };

    existing.initiative_count += 1;
    if (initiative.totalEstHc != null) {
      existing.total_headcount = (existing.total_headcount ?? 0) + initiative.totalEstHc;
      totalHeadcount += initiative.totalEstHc;
      hasHeadcount = true;
    }
    const category = initiative.investmentCategory?.trim() || UNCATEGORIZED_INVESTMENT_CATEGORY;
    const categoryDevWeeks = initiative.quarterEstimates[quarterEstimateKey] ?? 0;
    const categories = existing.investment_categories ?? {};
    categories[category] = (categories[category] ?? 0) + categoryDevWeeks;
    existing.investment_categories = categories;
    investmentCategories[category] = (investmentCategories[category] ?? 0) + categoryDevWeeks;
    const estimates = existing.dev_estimates ?? {};
    (['q1', 'q2', 'q3', 'q4'] as const).forEach((quarter) => {
      estimates[quarter] = (estimates[quarter] ?? 0) + (initiative.quarterEstimates[quarter] ?? 0);
    });
    existing.dev_estimates = estimates;
    workstreams.set(key, existing);
  });

  return {
    workstreams: [...workstreams.values()].sort((left, right) =>
      left.title.localeCompare(right.title, undefined, {numeric: true})
    ),
    totals: {
      initiative_count: initiatives.length,
      total_headcount: hasHeadcount ? totalHeadcount : null,
      investment_categories: investmentCategories,
    },
  };
};
